import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CampaignApiRecord,
  CampaignMutationResponse,
  UpdateCampaignRequest,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { mergeCampaignIntoLists } from "./campaignQueryCache";
import { unwrapGamePassEntity } from "./responseMappers";

const buildUpdateCampaignUrl = (campaignId: string): string =>
  API_ROUTES.UPDATE_CAMPAIGN.replace("{campaign_id}", campaignId);

export type UpdateCampaignVariables = {
  campaignId: string;
  body: UpdateCampaignRequest;
};

const putUpdateCampaign = async ({
  campaignId,
  body,
}: UpdateCampaignVariables): Promise<CampaignApiRecord> => {
  const response = await getPrivateInstance().put<CampaignMutationResponse>(
    buildUpdateCampaignUrl(campaignId),
    body,
  );
  return unwrapGamePassEntity(response.data, "campaign");
};

/**
 * Partial update of a Monthly Challenge campaign
 * (`PUT /gamepass/v2/admin/campaigns/{campaign_id}`). Send only changed fields;
 * mirrors `useUpdateSeason` (minus the wizard-specific mappers, which land in
 * the mapper ticket).
 */
export const useUpdateCampaign = (): UseMutationResult<
  CampaignApiRecord,
  Error,
  UpdateCampaignVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaigns", "update"],
    mutationFn: putUpdateCampaign,
    onSuccess: (campaign) => mergeCampaignIntoLists(queryClient, campaign),
  });
};
